/** @jsx jsx */
import React, {
	type Ref,
	forwardRef,
	type ButtonHTMLAttributes,
	type DetailedHTMLProps,
	type ReactNode,
	useCallback,
} from 'react';
import { cssMap, jsx } from '@compiled/react';
import { useFragment, graphql } from 'react-relay';
import { SpotlightPulse } from '@atlaskit/onboarding';
import { TabMenu } from '@atlassian/jira-horizontal-nav-tab-menu/src/ui/TabMenu.tsx';
import { useRenameNavigationItem } from '@atlassian/jira-horizontal-nav-tab-menu/src/services/rename-navigation-item/useRenameNavigationItem.tsx';
import type { TabWithMenu_container$key } from '@atlassian/jira-relay/src/__generated__/TabWithMenu_container.graphql';
import type { TabWithMenu_tab$key } from '@atlassian/jira-relay/src/__generated__/TabWithMenu_tab.graphql';
import TabUI from '@atlassian/navigation-kit-ui-tab';
import { fg } from '@atlassian/jira-feature-gating';

interface BaseTabProps {
	name: ReactNode;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onClick?: (event: any) => any;
	href?: string;
	isOpen?: boolean;
	isLoading?: boolean;
	elemBefore?: ReactNode;
	elemAfter?: ReactNode;
	isDraggable?: boolean;
	menu?: React.ComponentType<{ isOverflowing?: boolean }>;
	className?: string;
	testId?: string;
	/** @property position - position of the tab in the tablist. Uses 1 based positioning */
	position?: number;
	handleDrag?: () => void;
}

type AnchorProps = BaseTabProps & {
	type: 'anchor';
};

type ButtonPassthroughProps = Omit<
	DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
	'type' | 'name' | 'ref' | 'aria-selected'
>;

type DropdownTriggerProps = BaseTabProps & {
	type: 'dropdown-trigger';
	isMoreMenu?: boolean;
} & ButtonPassthroughProps;

type RouterLinkProps = BaseTabProps & {
	type?: 'router-link';
};

type TabProps = AnchorProps | DropdownTriggerProps | RouterLinkProps;

type Props = Omit<TabProps, 'name' | 'isDraggable' | 'menu'> & {
	tabFragment: TabWithMenu_tab$key;
	containerFragment: TabWithMenu_container$key;
	beforeItemId: string | null;
	afterItemId: string | null;
	isRenaming?: boolean;
	onRenamingChange?: (isRenaming: boolean) => void;
	testId?: string;
	shouldHighlight?: boolean;
	onDisableHighlight?: (tabKey: string) => void;
};
export const TabWithMenu = forwardRef(
	(
		{
			tabFragment,
			containerFragment,
			beforeItemId,
			afterItemId,
			isRenaming,
			onRenamingChange,
			shouldHighlight,
			onDisableHighlight,
			...tabProps
		}: Props,
		ref: Ref<HTMLElement>,
	) => {
		const renameNavigationItem = useRenameNavigationItem();
		const tabData = useFragment(
			graphql`
				fragment TabWithMenu_tab on JiraNavigationItem {
					id @required(action: THROW)
					label @required(action: THROW)
					typeKey @required(action: THROW)
					__typename
					...TabMenu_tab
				}
			`,
			tabFragment,
		);

		const containerData = useFragment(
			graphql`
				fragment TabWithMenu_container on JiraContainerNavigationResult {
					...TabMenu_container
					... on JiraContainerNavigation {
						canEdit
						scopeId
					}
				}
			`,
			containerFragment,
		);

		const canEdit = Boolean(containerData?.canEdit);

		const handleRenameComplete = useCallback(
			(newName: string) => {
				renameNavigationItem?.({
					id: tabData.id,
					label: newName,
					scopeId: containerData.scopeId,
					typeKey: tabData.typeKey,
					__typename: tabData.__typename,
				});
				onRenamingChange?.(false);
			},
			[
				renameNavigationItem,
				tabData.id,
				tabData.typeKey,
				tabData.__typename,
				containerData.scopeId,
				onRenamingChange,
			],
		);

		const handleRenameClick = useCallback(() => {
			onRenamingChange?.(true);
		}, [onRenamingChange]);

		const handleRenameCancel = useCallback(() => {
			onRenamingChange?.(false);
		}, [onRenamingChange]);

		const renderMenu = useCallback(
			({
				isOverflowing,
				isOpen: isMenuOpen,
				setIsOpen: setIsMenuOpen,
			}: {
				isOverflowing?: boolean | undefined;
				isOpen: boolean;
				setIsOpen: (setOpen: boolean | ((isOpen: boolean) => boolean)) => void;
			}) => (
				<TabMenu
					tabFragment={tabData}
					containerFragment={containerData}
					beforeItemId={beforeItemId}
					afterItemId={afterItemId}
					position={tabProps.position}
					isOverflowing={isOverflowing}
					isOpen={isMenuOpen}
					setIsOpen={setIsMenuOpen}
					onRenameClick={handleRenameClick}
				/>
			),
			[tabData, containerData, beforeItemId, afterItemId, tabProps.position, handleRenameClick],
		);

		if (fg('jfp_a11y_team_board_missing_unordered_list_mark-up')) {
			if (fg('grw-1800-auto-connect-space-gate'))
				return (
					<li css={[styles.root]}>
						<SpotlightPulse
							pulse={!!shouldHighlight}
							radius={3}
							onMouseEnter={() => onDisableHighlight?.(tabData.typeKey)}
						>
							<TabUI
								name={tabData.label}
								isDraggable={canEdit}
								renderMenu={canEdit ? renderMenu : undefined}
								onRenameComplete={handleRenameComplete}
								onRenameCancel={handleRenameCancel}
								isRenaming={isRenaming}
								{...tabProps}
								ref={ref}
							/>
						</SpotlightPulse>
					</li>
				);

			return (
				<li css={[styles.root]}>
					<TabUI
						name={tabData.label}
						isDraggable={canEdit}
						renderMenu={canEdit ? renderMenu : undefined}
						onRenameComplete={handleRenameComplete}
						onRenameCancel={handleRenameCancel}
						isRenaming={isRenaming}
						{...tabProps}
						ref={ref}
					/>
				</li>
			);
		}

		if (fg('grw-1800-auto-connect-space-gate'))
			return (
				<SpotlightPulse
					pulse={!!shouldHighlight}
					radius={3}
					onMouseEnter={() => onDisableHighlight?.(tabData.typeKey)}
				>
					<TabUI
						name={tabData.label}
						isDraggable={canEdit}
						renderMenu={canEdit ? renderMenu : undefined}
						onRenameComplete={handleRenameComplete}
						onRenameCancel={handleRenameCancel}
						isRenaming={isRenaming}
						{...tabProps}
						ref={ref}
					/>
				</SpotlightPulse>
			);

		return (
			<TabUI
				name={tabData.label}
				isDraggable={canEdit}
				renderMenu={canEdit ? renderMenu : undefined}
				onRenameComplete={handleRenameComplete}
				onRenameCancel={handleRenameCancel}
				isRenaming={isRenaming}
				{...tabProps}
				ref={ref}
			/>
		);
	},
);

const styles = cssMap({
	root: {
		marginTop: '0px',
		listStyle: 'none',
	},
});
